import React, { useState, useEffect } from "react";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";

// CASH pays into the withdrawable balance, BONUS into the bonus balance.
const REWARD_TYPES = ["CASH", "BONUS", "BOTH", "BONUS_PERCENT", "REFERRAL_BOOST"];

// The API returns dates as ISO-8601 carrying the server's offset. Convert to the
// "yyyy-MM-ddTHH:mm" a datetime-local input needs, in the admin's own timezone.
const toPickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value).substring(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Picker value -> absolute instant, so the promo window opens and closes at the
// moment the admin picked no matter which timezone anyone is in.
const toInstant = (value) => (value ? new Date(value).toISOString() : null);

const givesCash = (type) => ["CASH", "BOTH", "WELCOME", "REFERRAL"].includes(type);
const givesBonus = (type) => ["BONUS", "BOTH", "WELCOME", "REFERRAL"].includes(type);
const isBonusPercent = (type) => type === "BONUS_PERCENT";
// REFERRAL_BOOST reuses bonusPercentage, but applies to the next referral bonus
// rather than to a package purchase — so no package picker.
const isReferralBoost = (type) => type === "REFERRAL_BOOST";

const emptyForm = {
  promoCode: "",
  title: "",
  description: "",
  rewardType: "BONUS",
  cashRewardUsd: "",
  bonusRewardUsd: "",
  bonusPercentage: "",
  // Purchase gate: the player must have bought at least minPurchaseAmount
  // (USD) in the last purchasePeriodDays days before the code will pay out.
  minPurchaseAmount: "",
  purchasePeriodDays: 3,
  startDate: "",
  endDate: "",
  perUserLimit: 1,
  totalLimit: 0,
  active: true,
};

const CreatePromoCampaign = ({ open, onClose, editData = {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [packages, setPackages] = useState([]);
  const [bonusPackage, setBonusPackage] = useState(null); // single selected package

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Load coin packages once (for the BONUS_PERCENT package picker). /admin/packages
  // returns a raw array, so accept either that or a {data:[...]} wrapper.
  useEffect(() => {
    if (!open) return;
    getAllData(`${endPoints.api.GET_PACKAGE}`)
      .then((res) => setPackages(Array.isArray(res) ? res : res?.data || []))
      .catch((err) => console.error(err));
  }, [open]);

  // Fill the form from editData (or reset) each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (editData?.id) {
      setForm({
        ...emptyForm,
        ...editData,
        startDate: toPickerValue(editData.startDate),
        endDate: toPickerValue(editData.endDate),
      });
    } else {
      setForm(emptyForm);
      setBonusPackage(null);
    }
  }, [open, editData]);

  // Reconcile the selected package once packages are loaded (first id in the CSV).
  useEffect(() => {
    if (!editData?.bonusPackageIds || !packages.length) return;
    const firstId = String(editData.bonusPackageIds).split(",")[0].trim();
    setBonusPackage(packages.find((p) => String(p.id) === firstId) || null);
  }, [packages, editData]);

  const submit = () => {
    if (!form.promoCode.trim()) {
      ErrorToast("Promo code is required");
      return;
    }
    if (/\s/.test(form.promoCode)) {
      ErrorToast("Promo code cannot contain spaces");
      return;
    }
    if (!form.startDate) {
      ErrorToast("Start date is required");
      return;
    }
    if (!form.endDate) {
      ErrorToast("End date is required");
      return;
    }
    if (new Date(form.endDate) <= new Date(form.startDate)) {
      ErrorToast("End date must be after start date");
      return;
    }
    if (givesCash(form.rewardType) && !givesBonus(form.rewardType) && !(Number(form.cashRewardUsd) > 0)) {
      ErrorToast("Enter the cash reward amount in USD");
      return;
    }
    if (givesBonus(form.rewardType) && !givesCash(form.rewardType) && !(Number(form.bonusRewardUsd) > 0)) {
      ErrorToast("Enter the bonus reward amount in USD");
      return;
    }
    if (form.rewardType === "BOTH" && !(Number(form.cashRewardUsd) > 0) && !(Number(form.bonusRewardUsd) > 0)) {
      ErrorToast("Enter a cash or bonus amount in USD");
      return;
    }
    if (isBonusPercent(form.rewardType) && !(Number(form.bonusPercentage) > 0)) {
      ErrorToast("Enter the purchase bonus %");
      return;
    }
    if (isReferralBoost(form.rewardType) && !(Number(form.bonusPercentage) > 0)) {
      ErrorToast("Enter the referral boost %");
      return;
    }
    if (Number(form.minPurchaseAmount) > 0 && !(Number(form.purchasePeriodDays) > 0)) {
      ErrorToast("Purchase period must be at least 1 day");
      return;
    }
    const num = (v) => Number(v) || 0;
    const type = form.rewardType;
    const payload = {
      promoCode: form.promoCode.trim(),
      title: form.title,
      description: form.description,
      rewardType: type,
      // Both amounts are USD — the player is credited the equivalent in their own currency.
      cashRewardUsd: givesCash(type) ? num(form.cashRewardUsd) : 0,
      bonusRewardUsd: givesBonus(type) ? num(form.bonusRewardUsd) : 0,
      bonusPercentage: isBonusPercent(type) || isReferralBoost(type) ? num(form.bonusPercentage) : 0,
      bonusPackageIds: isBonusPercent(type) && bonusPackage ? String(bonusPackage.id) : null,
      // Purchase gate applies to every reward type. 0 = anyone may redeem.
      minPurchaseAmount: num(form.minPurchaseAmount),
      purchasePeriodDays: num(form.purchasePeriodDays) || 3,
      startDate: toInstant(form.startDate),
      endDate: toInstant(form.endDate),
      perUserLimit: num(form.perUserLimit),
      totalLimit: num(form.totalLimit),
      active: !!form.active,
    };
    if (editData?.id) payload.id = editData.id;

    const url = editData?.id ? endPoints.api.PROMO_CAMPAIGN_UPDATE : endPoints.api.PROMO_CAMPAIGN_ADD;
    setIsLoading(true);
    addData(url, payload)
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.status === "success") {
          SuccesToast(res?.data?.message || "Promotion saved");
          onClose();
        } else {
          ErrorToast(res?.data?.message || "Failed to save promotion");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        ErrorToast("Failed to save promotion");
      });
  };

  const field = { width: "100%", marginTop: "16px" };
  const type = form.rewardType;

  return (
    <Dialog maxWidth="md" fullWidth open={open}>
      <DialogTitle sx={{ pr: 6 }}>
        {editData?.id ? "Edit" : "Create"} Promo Code
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8, color: "#64607a" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Toaster />
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ marginTop: "8px" }}>
              Reward amounts are entered in <b>USD</b>. Each player is credited the equivalent
              in their own currency (fiat or crypto) at the exchange rate when they redeem.
            </Alert>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Promo Code *" variant="outlined" value={form.promoCode}
              helperText="No spaces allowed"
              onChange={(e) => setForm({ ...form, promoCode: e.target.value.toUpperCase().replace(/\s/g, "") })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} select label="Reward Type *" variant="outlined" value={type} onChange={set("rewardType")}>
              {REWARD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Title" variant="outlined" value={form.title} onChange={set("title")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Description" variant="outlined" value={form.description} onChange={set("description")} />
          </Grid>

          {givesCash(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Cash Reward (USD)" variant="outlined"
                helperText="Paid into the withdrawable balance"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                value={form.cashRewardUsd} onChange={set("cashRewardUsd")} />
            </Grid>
          )}
          {givesBonus(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Bonus Reward (USD)" variant="outlined"
                helperText="Paid into the bonus balance"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                value={form.bonusRewardUsd} onChange={set("bonusRewardUsd")} />
            </Grid>
          )}

          {isBonusPercent(type) && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField sx={field} type="number" label="Bonus % on purchase" variant="outlined"
                  helperText="Credited to the bonus balance when a qualifying package is bought"
                  value={form.bonusPercentage} onChange={set("bonusPercentage")} />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  sx={field}
                  options={packages}
                  value={bonusPackage}
                  getOptionLabel={(p) => `${p?.package_name || "Package"} ($${p?.purchase_price})`}
                  isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                  onChange={(e, value) => setBonusPackage(value)}
                  renderInput={(params) => <TextField {...params} label="Bonus applies to this package (blank = any)" variant="outlined" />}
                />
              </Grid>
            </>
          )}

          {isReferralBoost(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Extra % on next referral bonus" variant="outlined"
                inputProps={{ min: 1, max: 500 }}
                helperText="Applied once, to the next referral that qualifies — not per referral"
                value={form.bonusPercentage} onChange={set("bonusPercentage")} />
            </Grid>
          )}

          <Grid item xs={12}>
            <Alert severity={Number(form.minPurchaseAmount) > 0 ? "warning" : "info"} sx={{ marginTop: "16px" }}>
              {Number(form.minPurchaseAmount) > 0
                ? `Purchase required — only players who bought $${Number(form.minPurchaseAmount).toFixed(2)} or more in the last ${Number(form.purchasePeriodDays) || 3} day(s) can claim this code. The player sees the amount in their own currency.`
                : "No purchase requirement — any registered player can claim this code."}
            </Alert>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Min Purchase Amount (USD)" variant="outlined"
              inputProps={{ min: 0, step: "0.01" }}
              helperText="Leave blank or 0 for no purchase requirement"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              value={form.minPurchaseAmount} onChange={set("minPurchaseAmount")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Purchase Period (days)" variant="outlined"
              inputProps={{ min: 1 }}
              helperText="How far back purchases are counted. Defaults to 3"
              disabled={!(Number(form.minPurchaseAmount) > 0)}
              value={form.purchasePeriodDays} onChange={set("purchasePeriodDays")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="datetime-local" label="Start Date *" variant="outlined"
              InputLabelProps={{ shrink: true }} value={form.startDate} onChange={set("startDate")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="datetime-local" label="End Date *" variant="outlined"
              InputLabelProps={{ shrink: true }} value={form.endDate} onChange={set("endDate")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Per-User Limit" variant="outlined"
              value={form.perUserLimit} onChange={set("perUserLimit")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Total Limit (0 = unlimited)" variant="outlined"
              value={form.totalLimit} onChange={set("totalLimit")} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label="Active"
              sx={{ marginTop: "16px" }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit}>{editData?.id ? "Update" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePromoCampaign;
